import { getProductImage } from "~/utils/productUtils";
import { ImageFormat } from "~/domains/product/types/ProductDataDTO";
import { Card } from "~/features/configurator/Linear/components/Card";
import ProductDesignation from "~/features/productDetails/components/ProductDesignation";
import classNames from "classnames";
import CenterContents from "~/common/components/CenterContents";
import { styled } from "~/common/mui";
import { type RelatedProductData } from "~/features/productDetails/components/RelatedProducts/RelatedProductsBlock";
import Parameter from "~/common/components/Parameter";
import { trackPdpRelatedProductsClick } from "~/app/analytics/ga/ga";
import { IconSize } from "~/common/components/icon/IconSize";
import { SchaefflerIdParameter } from "~/domains/product/components/ProductParams";
import { fakeDescriptorFromProduct } from "~/domains/product/utils/productDescriptorUtils";
import { type ProductData } from "~/domains/product/types/ProductData";
import { Translation } from "~/app/i18n/Translation";
import { useTranslation } from "react-i18next";
import { appendPurchaseParams } from "~/features/productDetails/components/ProductSummary/PdpSummaryHelper";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import type { PurchaseType } from "~/app/analytics/ga/recommendations/types";
import { Product } from "~/domains/product/Product";
import { ProductContext } from "~/domains/product/ProductContext";
import { Stack } from "~/common/mui/Stack/Stack";

interface RelatedProductProps {
  mainProductDesignation: string;
  product: RelatedProductData;
}

export const MobileRelatedProductCard = ({ product, mainProductDesignation }: RelatedProductProps) => {
  const reco_place = usePurchasePlace();
  const reco_type: PurchaseType = "rp";
  let onClick = undefined;
  let href = undefined;
  if (product.url) {
    onClick = () => trackPdpRelatedProductsClick(mainProductDesignation, product.designation);
    href = appendPurchaseParams(product.url, reco_place, reco_type);
  }

  const image = getProductImage(product, ImageFormat.thumbnail);

  return (
    <ProductContext value={product as ProductData}>
      <Card
        className={"scha__related-product__card"}
        disableHover={!product.url}
        as={product.url ? "a" : "div"}
        href={href}
        onClick={onClick}
      >
        <AspectRatioImage src={image?.url} />
        <CardContent>
          <ProductDesignation
            className={"scha__related-product__card__designation"}
            isPreferred={product.preferred}
            designation={product.designation}
            size={"title-medium"}
            iconSize={IconSize.FONT_16}
          />
          <Stack gap={1}>
            <p className={"scha__related-product__card__description"}>
              <Translation {...product.shortDescription} />
            </p>
            {window.SCHA.feature.relatedProductPDPAvailability && (
              <Product.StockIndicator className="scha__related-product__card__stock" />
            )}
          </Stack>
        </CardContent>
      </Card>
    </ProductContext>
  );
};

export const DesktopRelatedProductCard = ({ product, mainProductDesignation }: RelatedProductProps) => {
  const reco_place = usePurchasePlace();
  const reco_type: PurchaseType = "rp";
  let onClick = undefined;
  let href = undefined;
  if (product.url) {
    onClick = () => trackPdpRelatedProductsClick(mainProductDesignation, product.designation);
    href = appendPurchaseParams(product.url, reco_place, reco_type);
  }

  const image = getProductImage(product, ImageFormat.thumbnail);
  const { t } = useTranslation();

  // TODO: Send packageAliases via PDP and fix this
  const customerMaterialNumber = (product.aliases || []).join(", ");
  return (
    <ProductContext value={product as ProductData}>
      <Card
        className={"scha__related-product__card"}
        disableHover={!product.url}
        as={product.url ? "a" : "div"}
        href={href}
        onClick={onClick}
      >
        <div className={"scha__related-product__card__image"}>{image && <img src={image.url} alt="" />}</div>
        <div>
          <ProductDesignation
            className={"scha__related-product__card__designation"}
            isPreferred={product.preferred}
            designation={product.designation}
            size={"title-medium"}
            iconSize={IconSize.FONT_16}
          />
          <Stack gap={1}>
            <p className={"scha__related-product__card__description"}>
              <Translation {...product.shortDescription} />
            </p>
            {window.SCHA.feature.relatedProductPDPAvailability && (
              <Product.StockIndicator className="scha__related-product__card__stock" />
            )}
            <SchaefflerIdParameter productDescriptor={fakeDescriptorFromProduct(product as ProductData)} />
            {customerMaterialNumber && <Parameter label={t("addProduct.customerId")} value={customerMaterialNumber} />}
          </Stack>
        </div>
      </Card>
    </ProductContext>
  );
};

interface AspectRatioImageProps {
  className?: string;
  src?: string;
  alt?: string;
}

/**
 * Image with a 16:9 ratio. In case src is not provided, it will still occupy space
 */
const AspectRatioImage = ({ className, src, alt }: AspectRatioImageProps) => {
  return (
    <div className={classNames("forced-ratio-container--9-16", className)}>
      <CenterImage>{src && <img src={src} alt={alt} />}</CenterImage>
    </div>
  );
};

const CenterImage = styled(CenterContents)({
  padding: "8px",
});

const CardContent = styled("div")({
  padding: "12px",
});
